/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { Navigate } from 'react-router-dom'

import { useAuth } from '@/hooks'
import { firstAppPath } from '@/utils/appHome'

import { ColdStorage } from './ColdStorage'
import { ColdStorageProvider } from './ColdStorageContext'
import type { ColdStorageMode } from './mode'

/**
 * Cold Storage: the in-store assets (cold rooms, freezers, loading docks — "locations")
 * a company monitors with tags, laid out on an uploaded floor plan.
 *
 * Blast Freezer (`mode="blastFreezer"`) is the same page narrowed to freezers, with
 * each sensor's time in range.
 *
 * Both are gated on the `coldStorage` app grant. The routes carry no guards of their
 * own, so the page checks the same permission the nav link does; the API enforces it
 * as well.
 */
export const ColdStoragePage: React.FC<{ mode?: ColdStorageMode }> = ({ mode = 'coldStorage' }) => {
  const { hasPermission } = useAuth()
  if (!hasPermission('coldStorage')) {
    return <Navigate to={firstAppPath(hasPermission)} replace />
  }
  return (
    <ColdStorageProvider mode={mode}>
      <ColdStorage />
    </ColdStorageProvider>
  )
}
